/**
 * MeekoConfig — Configuration connexion Meeko (email/password chiffres)
 */

import { useState, useEffect } from 'react';
import { Settings, Save, Loader, Check, AlertCircle, RefreshCw } from 'lucide-react';
import { getMeekoSettings, updateMeekoSettings, testMeekoConnection } from '../../api';
import { useToast } from '../../contexts/NotificationContext';

const MeekoConfig = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState({ email: '', password: '', enabled: true });
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getMeekoSettings();
        setSettings({
          email: data.email || '',
          password: '',
          enabled: data.enabled,
          hasCredentials: data.hasCredentials,
          lastSyncAt: data.lastSyncAt,
          lastSyncCount: data.lastSyncCount,
        });
      } catch (e) {
        console.error('[MeekoConfig] load:', e);
      }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { enabled: settings.enabled };
      if (settings.email && !settings.email.startsWith('••')) payload.email = settings.email;
      if (settings.password) payload.password = settings.password;
      await updateMeekoSettings(payload);
      toast.success('Configuration Meeko sauvegardee');
      // Reload
      const { data } = await getMeekoSettings();
      setSettings(s => ({ ...s, email: data.email, password: '', hasCredentials: data.hasCredentials }));
    } catch (e) {
      toast.error('Erreur sauvegarde');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await testMeekoConnection();
      setTestResult(data);
      toast.success('Connexion Meeko OK');
    } catch (e) {
      setTestResult({ success: false, error: e.response?.data?.error || 'Erreur connexion' });
      toast.error('Connexion echouee');
    }
    setTesting(false);
  };

  if (loading) return <div className="px-4 sm:px-6 lg:px-8 text-center py-8 text-gray-400"><Loader className="animate-spin mx-auto" size={24} /></div>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <Settings size={20} className="text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-800">Configuration Meeko</h2>
      </div>
      <p className="text-sm text-gray-500">
        Configurez les identifiants de connexion Meeko. Les credentials sont chiffres en base de donnees (AES-256-GCM).
      </p>

      {/* Formulaire */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Meeko</label>
            <input
              type="email"
              value={settings.email}
              onChange={e => setSettings(s => ({ ...s, email: e.target.value }))}
              placeholder="jerome.delvalle@mc-jde.fr"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe Meeko</label>
            <input
              type="password"
              value={settings.password}
              onChange={e => setSettings(s => ({ ...s, password: e.target.value }))}
              placeholder={settings.hasCredentials ? '••••••••••••••••' : 'Mot de passe'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
            />
            {settings.hasCredentials && !settings.password && (
              <p className="text-xs text-gray-400 mt-1">Laissez vide pour conserver le mot de passe actuel.</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            id="meeko-enabled"
          />
          <label htmlFor="meeko-enabled" className="text-sm text-gray-700">Synchronisation activee</label>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Enregistrer
          </button>
          <button
            onClick={handleTest}
            disabled={testing}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm font-medium"
          >
            {testing ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Tester la connexion
          </button>
        </div>
      </div>

      {/* Resultat test */}
      {testResult && (
        <div className={`rounded-lg p-4 text-sm ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          {testResult.success ? (
            <div>
              <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                <Check size={16} /> Connexion reussie
              </div>
              <p className="text-green-600">
                Connecte en tant que <strong>{testResult.user?.firstName} {testResult.user?.lastName}</strong>
              </p>
              {testResult.nurseries && (
                <div className="mt-2">
                  <p className="text-green-600 text-xs font-medium">Structures Meeko :</p>
                  {testResult.nurseries.map(n => (
                    <p key={n.id} className="text-green-600 text-xs ml-2">• {n.name}</p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle size={16} /> {testResult.error}
            </div>
          )}
        </div>
      )}

      {/* Dernière synchro */}
      {settings.lastSyncAt && (
        <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
          Derniere synchronisation : <strong>{new Date(settings.lastSyncAt).toLocaleString('fr-FR')}</strong>
          {settings.lastSyncCount !== null && <> — {settings.lastSyncCount} enfant(s) synchronise(s)</>}
        </div>
      )}
    </div>
  );
};

export default MeekoConfig;
